import React, { useState, useEffect, useRef, memo } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Switch,
  FormControlLabel,
  Collapse,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useApi } from "../../../services/useApi";
import { ApiSuccessResponse, ApiErrorResponse } from "../../../types/api";
import {
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  Update as UpdateIcon,
  Block as BlockIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import DataTable from "../../../components/common/DataTable";
import MappingCell from "../components/MappingCell";
import MappingDialogs from "../components/MappingDialogs";
import { deepClean } from "../../../utils/objectUtils";
import { useCommonOptions } from '../../../hooks/useCommonOptions';
import SubmissionReportDialog, { SubmissionReport, BatchError } from '../components/SubmissionReportDialog';
import { useDynamicMappings } from '../../../hooks/useDynamicMappings';
import ValueCell from "../components/ValueCell";
import OneSnackbar from "../components/OneSnackbar";
import SubmitOptions from "../components/SubmitOptions";
import {MappingSubmitResponse, BatchRecordError, UpdateIfExistsMap} from '../../,,/../../interfaces/CommonInterface';
import { scrollToErrorRow, validateMapping } from "../../../hooks/useCommonValidate";
import UploadData from "../components/UploadData";
import DataSourceTable from "../components/DataSourceTable";
import { useAutoMapping } from "../../../hooks/useAutoMapping";


interface EconomicProduct {
  index?: number;
  productNumber: string,
  barCode: string;
  costPrice: string;
  departmentalDistribution:{
    departmentalDistributionNumber: string;
  },
  description: string,
  name: string,
  productGroup:{
    productGroupNumber: string;
  },
  salesPrice:string,
  unit:{
    unitNumber: string;
    originalValue: string;
  }
}

interface ProductPanelProps {}

interface Mapping {
  barCode: string;
  costPrice: string;
  departmentalDistribution_departmentalDistributionNumber: string;
  description: string;
  name: string;
  productGroup_productGroupNumber: string;
  salesPrice: string;
  unit_unitNumber: string;
  productNumber: string;

}

interface DefaultValues extends Mapping {}


const ProductPanel: React.FC<ProductPanelProps> = () => {
  const [skipLines, setSkipLines] = useState<number>(0);
  const [sourceData, setSourceData] = useState<any[][]>([]);
  const [originalSourceData, setOriginalSourceData] = useState<any[][]>([]);
  const [sourceColumns, setSourceColumns] = useState<
    { id: string; label: string }[]
  >([]);
  const [economicProducts, setEconomicProducts] = useState<EconomicProduct[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | string[] | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping>({
    barCode: '',
    costPrice: '',
    departmentalDistribution_departmentalDistributionNumber: '',
    description: '',
    name: '',
    productGroup_productGroupNumber: '',
    salesPrice: '',
    unit_unitNumber: '',
    productNumber: ''
  });
  const [batchSize] = useState(10);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    barCode: "",
    costPrice: "",
    departmentalDistribution_departmentalDistributionNumber: "",
    description: "",
    name: "",
    productGroup_productGroupNumber: "",
    salesPrice: "",
    unit_unitNumber: "",
    productNumber: "",
  });
  const [updateExistingProduct, setUpdateExistingProduct] = useState<boolean>(false);
  const [requiredFields] = useState<Array<keyof Mapping>>([
    'name',
    'productGroup_productGroupNumber',
    'unit_unitNumber',
    'productNumber'
  ]);
  const [updateKey, setUpdateKey] = useState<string>('');
  const [showReport, setShowReport] = useState(false);
  const [submissionReport, setSubmissionReport] = useState<SubmissionReport | null>(null);
  const [errorRecords, setErrorRecords] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);
  const [updateIfExistsMap, setUpdateIfExistsMap] = useState<UpdateIfExistsMap>({});

  const api = useApi();
  const { units, departmentalDistributions, productGroups } = useCommonOptions();
  const { getMappedValue: getDynamicMappedValue} = useDynamicMappings();



  const [sequenceInfo, setSequenceInfo] = useState<{ 
    prefix: string; 
    startNumber: number;
    numberLength: number;
  } | null>(null);
  const [currentField, setCurrentField] = useState<string | null>(null);

  const handleSequenceChange = (field: string, value: string) => {
    const match = value.match(/^([A-Za-z]*)(\d+)$/);
    if (match) {
      const [, prefix, numberStr] = match;
      const numberLength = numberStr.length;
      const startNumber = parseInt(numberStr, 10);
      if (!isNaN(startNumber)) {
        setCurrentField(field);
        setSequenceInfo({
          prefix,
          startNumber: startNumber,
          numberLength
        });
      }
    }else{
      setCurrentField(field);
      setSequenceInfo(null);
    }

    // const match = sequence.prefix.match(/^([A-Za-z]*)(\d+)$/);
    
    // if (match) {
    //   const [, prefix, number] = match;
    //   const numberLength = number.length;
    //   setCurrentField(field);
    //   setSequenceInfo({
    //     prefix,
    //     startNumber: parseInt(number),
    //     numberLength
    //   });
    //   // Immediately update the mapping when sequence changes
    //   // mapDineroToEconomic();
    // }
  };
  useEffect(() => {
    if (sourceData.length > 0) {
      mapSourceToEconomic(sourceData);
    }
  }, [skipLines, sourceData]);

  const handleSkipLinesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    setSkipLines(value);
  };
  


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getDefaultValue = (field: keyof DefaultValues) => {
    return defaultValues[field];
  };

  const mapSourceToEconomic = (sourceData: any[][]) => {
    const dataToProcess = sourceData.slice(skipLines);
    const mappedProducts = dataToProcess.map((row,index) => {
      function getMappedValue(field: string): {
        mapped: string | null;
        original: string | null;
      } {
        if (currentField === field && sequenceInfo) {
          
          const newNumber = sequenceInfo.startNumber + index;
          // Format number with leading zeros based on detected length
          const formattedNumber = newNumber.toString().padStart(sequenceInfo.numberLength, '0');
          return {
            mapped: `${sequenceInfo.prefix}${formattedNumber}`,
            original: null
          };
        }

        const def = getDefaultValue(field as keyof DefaultValues);
        if (def) return { mapped: def, original: null };

        if (field in mapping) {
          const key = mapping[field];
          if (key) {
            // Convert key to column index (assuming key is in format "col0", "col1", etc.)
            const colIndex = parseInt(key.replace("col", ""));
            if (!isNaN(colIndex) && colIndex < row.length) {
              const raw = row[colIndex];
              const mapped = getDynamicMappedValue(field, raw);
              return { mapped, original: raw };
            }
          }
        }
        return { mapped: null, original: null };
      }
      
      const { mapped: unitUnitNumber, original: unitUnitNumberOriginal } = getMappedValue("unit_unitNumber");
    
      return {
        index: index + skipLines,
        productNumber:getMappedValue("productNumber").mapped,
        barCode:getMappedValue("barCode").mapped,
        costPrice:getMappedValue("costPrice").mapped,
        departmentalDistribution:{
          departmentalDistributionNumber:getMappedValue("departmentalDistribution_departmentalDistributionNumber").mapped,
        },
        description:getMappedValue("description").mapped,
        name:getMappedValue("name").mapped,
        productGroup:{
          productGroupNumber:getMappedValue("productGroup_productGroupNumber").mapped,
        },
        salesPrice:getMappedValue("salesPrice").mapped,
        unit:{
          unitNumber:unitUnitNumber,
          originalValue:unitUnitNumberOriginal,
        }
      };
    });
    setEconomicProducts(mappedProducts);
    setSuccess("Successfully mapped " + mappedProducts.length + " products");  
  };


  const handleUpdateIfExistsChange = (field: string, value: boolean) => {
    setUpdateIfExistsMap(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitMapping = async () => {
    // Validate required fields
    const validationErrors = validateMapping(mapping, defaultValues, requiredFields);
    if (validationErrors.length > 0) {
      setError(validationErrors);
      return;
    }

    if (updateExistingProduct && !updateKey) {
      setError("Please select a key for updating existing products");
      return;
    }

    setIsSubmitting(true);
    setSubmitProgress(0);
    setError(null);
    setSuccess(null);
    setErrorRecords(new Set()); // Reset error records
    setShouldStop(false); // Reset stop flag
    setProcessedCount(0); // Reset processed count

    // Create new AbortController for this submission
    abortControllerRef.current = new AbortController();

    const totalRecords = economicProducts.length;
    const batches = Math.ceil(totalRecords / batchSize);
    setTotalBatches(batches);

    let successCount = 0;
    let failedCount = 0;
    let allErrors: (string | BatchRecordError)[] = [];

    try {
      for (let i = 0; i < batches; i++) {
        // Check if should stop
        if (shouldStop) {
          break;
        }

        const start = i * batchSize;
        const end = Math.min(start + batchSize, totalRecords);
        const batchProducts = economicProducts.slice(start, end);

        const filteredBatch = batchProducts
          .map(product => deepClean(product))
          .filter(item => item && Object.keys(item).length > 0);

        const response = await api.post<MappingSubmitResponse>(
          "/main/create-product",
          {
            mapping,
            products: filteredBatch,
            batchInfo: {
              currentBatch: i + 1,
              totalBatches: batches,
            },
            updateIfExists: updateIfExistsMap
          },
          {
            signal: abortControllerRef.current.signal
          }
        );

        const batchResponse = response.data.data;
        successCount += batchResponse.successCount;
        failedCount += batchResponse.failedCount;
        setProcessedCount(successCount); // Update processed count

        if (batchResponse.errors && batchResponse.errors.length > 0) {
          allErrors = [...allErrors, ...batchResponse.errors];
          batchResponse.errors.forEach(error => {
            setErrorRecords(prev => new Set(prev).add(error.recordId));
          });
        }

        setCurrentBatch(i + 1);
        setSubmitProgress(Math.round(((i + 1) / batches) * 100));
      }

      // Only process errors and show report if not stopped
      if (!shouldStop) {
        const reportErrors: (string | BatchError)[] = [];
        allErrors.forEach(error => {
          if (typeof error === 'string') {
            reportErrors.push(error);
          } else {
            error.errors.forEach(err => {
              reportErrors.push({
                recordId: error.recordId,
                field: '',
                message: err,
                value: ''
              });
            });
          }
        });

        setSubmissionReport({
          successCount,
          failedCount,
          errors: reportErrors
        });
        setShowReport(true);

        if (reportErrors.length > 0) {
          setError(
            `Failed to process ${failedCount} records. Click "View Report" for details.`
          );
        } else {
          const successMessage = `Successfully processed ${successCount} records.`;
          setSuccess(successMessage);
        }
      }
    } catch (error) {

      if (error.status === 500 && error.message === 'canceled') {
         setShowReport(true);
         setShouldStop(false);
        // Don't show message here as it's already shown in handleStopSubmit
      } else {
        setError("Failed to submit mapping. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
      abortControllerRef.current = null;
    }
  };

  const handleStopSubmit = () => {
    setShouldStop(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Show stop message immediately
    setSuccess(`Process stopped. Successfully processed ${processedCount} records.`);
    setIsSubmitting(false);
    setSubmitProgress(0);
    abortControllerRef.current = null;
  };

  const columnMappings = {
    productNumber: [
      "product number",
      "product no",
      "product id",
      "item number",
      "item no",
      "item id",
      "no.",
      "ProduktNr",
      'ProductNo',
      'VareNr'
    ],
    name: [
      "name",
      "product name",
      "item name",
      "Navn",
    ],
    productGroup_productGroupNumber: [
      "group",
      "product group",
      "item group",
      "category",
      "ProduktGruppe",
      'VareGruppe',
      'ProductGroup'
    ],
    unit_unitNumber: [
      "unit",
      "unit of measure",
      "uom",
      "Enhed",
    ],
    salesPrice: [
      "sales price",
      "price",
      "selling price",
      "SalgsPris",
      'SalesPrice',
      'Sales Price'
    ],
    costPrice: [
      "cost price",
      "cost",
      "purchase price",
      "KostPris",
      'CostPrice',
      'Cost Price'
    ],
    barCode: [
      "barcode",
      "bar code",
      "ean",
      "upc",
      "StregKode",
    ],
    departmentalDistribution_departmentalDistributionNumber: [
      "department",
      "departmental",
      "departmental distribution",
      "Afdeling",
    ],
    description: [
      "description",
      "desc",
      "notes",
      "Beskrivelse",
      
    ],
  };

  const { handleAutoMapFirstRow, autoMappingDone, setAutoMappingDone } = useAutoMapping<Mapping>({
    sourceData,
    originalSourceData,
    mapping,
    setMapping,
    setSourceColumns,
    setSuccess,
    setSkipLines,
    onSourceDataUpdate: (newData) => {
      setSourceData(newData);
      setTotalCount(newData.length);
    },
    columnMappings,
  });

  // Add useEffect to handle mapping updates
  useEffect(() => {
    if (autoMappingDone && sourceData.length > 0) {
      mapSourceToEconomic(sourceData);
      setAutoMappingDone(false);
    }
  }, [mapping, autoMappingDone, sourceData]);

  return (
    <>
        <UploadData
        onLoading={(loading) => {
          setLoading(loading);
        }}
        onLoadData={(data, columns) => {
          setOriginalSourceData(data);
          setSourceData(data);
          setSourceColumns(columns);
          setTotalCount(data.length);
          mapSourceToEconomic(data.slice(skipLines));
        }}
        onError={(msg) => {
          setError(msg);
        }}
      />
      <MappingDialogs
          mapping={mapping}
          defaultValues={defaultValues}
          type="product"
          onApplyMapping={(newMap, defaults) => {
            setMapping(newMap);
            setDefaultValues(defaults);
          }}
          onSuccess={(msg) => setSuccess(msg)}
      />

      
    <SubmitOptions
        mapSourceDataToEconomic={mapSourceToEconomic}
        sourceData={sourceData}
        economicData={economicProducts}
        isSubmitting={isSubmitting}
        handleSubmitMapping={handleSubmitMapping}
        handleStopSubmit={handleStopSubmit}
        submissionReport={submissionReport}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        submitProgress={submitProgress}
        setShowReport={setShowReport}
        skipLines={skipLines}
        handleSkipLinesChange={handleSkipLinesChange}
        onAutoMapFirstRow={handleAutoMapFirstRow}
      />
      <DataSourceTable
        sourceColumns={sourceColumns}
        sourceData={sourceData}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        skipLines={skipLines}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
      
      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main", mt:2 }}
      >
          E-conomic Products
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }} ref={tableRef}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            "& th, & td": {
              whiteSpace: "nowrap",
            },
          }}
          aria-label="economic customer table"
        >
          <TableHead>
            <TableRow>
            <MappingCell
                key="productNumber"
                label="Product No."
                field="productNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns} 
                requiredFields={requiredFields}
                onSequenceChange={(sequence) => handleSequenceChange('productNumber', sequence)}
                onUpdateIfExistsChange={(value) => handleUpdateIfExistsChange('productNumber', value)}
                showSequence={true}
              />
              <MappingCell
                key="name"
                label="Name"
                field="name"
                mapping={mapping}
                defaultValues={defaultValues}   
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) => handleUpdateIfExistsChange('name', value)}
                showSequence={true}
              />
              
              <MappingCell
                key="productGroup"
                label="Group"
                field="productGroup_productGroupNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns} 
                requiredFields={requiredFields}
                itemOptions={productGroups}
              />

              <MappingCell
                key="unit"
                label="Unit"
                field="unit_unitNumber"
                mapping={mapping}
                defaultValues={defaultValues} 
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={units}
              />

              <MappingCell
                key="salesPrice"
                label="Sales Price"
                field="salesPrice"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="costPrice"
                label="Cost Price"
                field="costPrice"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="barCode"
                label="Bar Code"
                field="barCode"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="Departmental"
                label="Departmental"
                field="departmentalDistribution_departmentalDistributionNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={departmentalDistributions}
              />
              <MappingCell
                key="description"
                label="Description"
                field="description"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              

            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ padding: "8px" }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : economicProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ padding: "8px" }}>
                  <Typography variant="body2">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              economicProducts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((product) => {
                  const hasError = errorRecords.has(
                    product?.index?.toString() || ""
                  );
                  return (
                    <TableRow 
                      key={product.index}
                      id={`row-${product.index}`}
                      sx={{
                        backgroundColor: hasError ? 'rgba(255, 0, 0, 0.1)' : undefined,
                        '&:hover': {
                          backgroundColor: hasError ? 'rgba(255, 0, 0, 0.2)' : undefined
                        }
                      }}
                    >
                      <ValueCell value={product.productNumber}/>
                      <ValueCell value={product.name}/>
                      <ValueCell value={product.productGroup.productGroupNumber} options={productGroups} />
                      <ValueCell value={product.unit.unitNumber} originalValue={product.unit.originalValue} options={units} />
                      <ValueCell value={product.salesPrice}/>
                      <ValueCell value={product.costPrice}/>  
                      <ValueCell value={product.barCode}/>
                      <ValueCell value={product.departmentalDistribution.departmentalDistributionNumber} options={departmentalDistributions} />
                      <ValueCell value={product.description}/>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            zIndex: 1,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem",
              },
          }}
        />
      </TableContainer>
      
      <OneSnackbar
        error={error}
        success={success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      />

      <SubmissionReportDialog
        open={showReport}
        onClose={() => setShowReport(false)}
        report={submissionReport}
        onErrorClick={(recordId) => {
          scrollToErrorRow(recordId, economicProducts, rowsPerPage, page, setPage, tableRef);
        }}
      />
    </>
  );
};

export default memo(ProductPanel);
